import { getBasePath } from "./pathConfig";

/**
 * Shared rules — the client's common Game Rules sections (2026-10-06).
 *
 * @popiplay/shared-translations ships them as small HTML files in
 * `shared-rules/` (how_to_play, autoplay, spin_speed, max_bet, settings, rtp,
 * rng, additional_info, translation_priority). Webpack copies the whole
 * package into the build at /shared-translations/, and the prebuild script
 * keeps it on the latest version — so a client edit reaches the game with no
 * change here.
 *
 * A game's rules.html asks for one with a marker where it belongs:
 *
 *   <!-- shared:autoplay -->
 *
 * and only writes its OWN sections (About the Game, Features, Spin Results).
 * A file that cannot be loaded leaves its section out; the rules still open.
 */

const SHARED_RULE_MARKER = /<!--\s*shared:([a-z0-9_]+)\s*-->/g;

/** Where one shared rule is served. */
// export const getSharedRulePath = (name) =>
//   `${getBasePath()}/shared-translations/shared-rules/${name}.html`;
// ── Translated rules (1.0.297, 2026-10-19) ── the package now ships every
// section per language in shared-rules/locales/<locale>/; English stays at
// the top level. Locale names as the package's (en-SOCIAL, pt-BR, zh-CN…).
export const getSharedRulePath = (name, locale = "en") => {
  const base = `${getBasePath()}/shared-translations/shared-rules`;
  const lng = locale === "en-social" ? "en-SOCIAL" : locale;
  return !lng || lng === "en" ? `${base}/${name}.html` : `${base}/locales/${lng}/${name}.html`;
};

/**
 * Fill the shared rules' own placeholders.
 *
 * @param {string} html
 * @param {{ rtp?: string|number, rulesVersion?: string, rulesDate?: string }} values
 */
const fillSharedRule = (html, { rtp = "", rulesVersion = "", rulesDate = "" }) =>
  html
    .replace(/{RTP_PLACEHOLDER}/g, String(rtp))
    .replace(/{RULES_VERSION}/g, String(rulesVersion))
    .replace(/{RULES_DATE}/g, String(rulesDate));

/**
 * Replace every `<!-- shared:<name> -->` marker in `html` with that shared
 * rule's HTML (fetched in parallel, each once).
 *
 * @param {string} html the game's rules.html
 * @param {{ rtp?: string|number, rulesVersion?: string, rulesDate?: string, locale?: string }} values
 * @returns {Promise<string>}
 */
export const inlineSharedRules = async (html, values = {}) => {
  if (!html || typeof html !== "string") return html;
  const names = [...new Set([...html.matchAll(SHARED_RULE_MARKER)].map((m) => m[1]))];
  if (!names.length) return html;

  const parts = await Promise.all(
    names.map(async (name) => {
      // try {
      //   const response = await fetch(getSharedRulePath(name));
      //   return response.ok ? await response.text() : "";
      // } catch {
      //   return "";
      // }
      // The player's language first; a missing or empty file falls back to
      // English, as the game's own rules.html does.
      const load = async (locale) => {
        try {
          const response = await fetch(getSharedRulePath(name, locale));
          return response.ok ? await response.text() : "";
        } catch {
          return "";
        }
      };
      const translated = values.locale && values.locale !== "en" ? await load(values.locale) : "";
      return translated.trim() ? translated : load("en");
    }),
  );
  const byName = new Map(names.map((name, i) => [name, fillSharedRule(parts[i], values)]));

  return html.replace(SHARED_RULE_MARKER, (_, name) => byName.get(name) ?? "");
};
